import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import Select from 'react-select'
import SignatureCanvas from 'react-signature-canvas'
import './style.css'
import {Form, Formik} from 'formik'
import toaster from '../../../../Utils/toaster'
import axios from 'axios'
import * as Yup from 'yup'
import {useRef, useState, useEffect} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {Modal} from 'bootstrap'
import {useParams} from 'react-router-dom'
import moment from 'moment'
import {readOsiBreakTimeMinutes} from './vosDurationUtils'
import {getFileType} from '../../../../Utils/commonData'
import {useAuth} from '../../../modules/auth'
import {VOSSignatureImage} from './OSISignatureImages'
import {formatVosTravelTime} from './vosFormatters'
const API_URL = process.env.REACT_APP_API_URL

const SUPPORTED_UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg'
const SUPPORTED_UPLOAD_MIME_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg'])
const VOS_UNREADABLE_MESSAGE = 'System was unable to fetch required details, Please review and attach VOS'
const VOS_READ_FALLBACK_MESSAGE = 'Could not read VOS form. You can still enter data manually and submit.'

function isSupportedUploadFile(file: File): boolean {
  if (SUPPORTED_UPLOAD_MIME_TYPES.has(file.type)) return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  return ext === 'pdf' || ext === 'png' || ext === 'jpg' || ext === 'jpeg'
}

function isSupportedUploadValue(value: unknown): boolean {
  if (value == null || value === '') return true
  if (typeof value === 'string') return true
  if (value instanceof File) return isSupportedUploadFile(value)
  return false
}

const HoursOptions: any = Array.from({length: 23}, (_, i) => ({
  value: i,
  label: String(i).padStart(2, '0'),
}))

const MinutesOptions: any = Array.from({length: 60}, (_, i) => ({
  value: i,
  label: String(i).padStart(2, '0'),
}))

type DigitalVosSource = 'client' | 'interpreter'

interface ModalProps {
  initialValues?: any
  fetchAfterSubmitOSISubmmission?: any
  expenseList: any
  setExpenseList: any
  isOsiEdit: boolean
  readOnly?: boolean
  /** When opened outside appointment viewer (e.g. bulk VOS finance). */
  appointmentCodeOverride?: number
  /** Bootstrap modal element id (default `#kt_add_osi_details`). */
  modalElementId?: string
  /** Bulk VOS finance: D-VOS layout (edit + submitted comparison) even for manual VOS. */
  forceDigitalVosEditLayout?: boolean
  /** Pre-select client vs interpreter card in digital VOS edit layout. */
  defaultDigitalVosSource?: DigitalVosSource
  appointmentDetails?: any
  payableHours?: number | null
  hourlyRate?: number | null
  meetingSummaryStatus?: number
  isResubmit?: boolean
  onRevertApproval?: () => void
  onEditWithoutUnlinking?: () => void
  isEditWithoutUnlinking?: boolean
  setIsEditWithoutUnlinking?: (val: boolean) => void
  setShowOsiReadOnly?: (val: boolean) => void
  meetingSummary?: any
  botRecordedTimes?: {start: string | null; end: string | null; timeZoneName: string | null} | null
  showUpdateAndApprove?: boolean
}

const formatVosDuration = (start: string | undefined, end: string | undefined): string => {
  if (!start || !end) return '-'
  const minutes = moment(end).diff(moment(start), 'minutes')
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** Digital requester VOS + bulk VOS portal client submission (stored on batch item, not RequesterVOSSubmissions). */
function resolveClientSubmittedVosDisplay(meetingSummary: any) {
  if (!meetingSummary) {
    return {show: false as const}
  }
  const hasRequester = Boolean(
    meetingSummary.hasRequesterVOSSubmission ?? meetingSummary.HasRequesterVOSSubmission
  )
  const bulkStart =
    meetingSummary.clientSubmittedStartDate ?? meetingSummary.ClientSubmittedStartDate
  const bulkEnd = meetingSummary.clientSubmittedEndDate ?? meetingSummary.ClientSubmittedEndDate
  const hasBulk = Boolean(bulkStart && bulkEnd)
  if (!hasRequester && !hasBulk) {
    return {show: false as const}
  }

  const start =
    meetingSummary.reqStartDate ??
    meetingSummary.ReqStartDate ??
    meetingSummary.requesterStartDate ??
    meetingSummary.RequesterStartDate ??
    bulkStart
  const end =
    meetingSummary.reqEndDate ??
    meetingSummary.ReqEndDate ??
    meetingSummary.requesterEndDate ??
    meetingSummary.RequesterEndDate ??
    bulkEnd
  const mileage =
    meetingSummary.requesterMilage ??
    meetingSummary.RequesterMilage ??
    meetingSummary.clientSubmittedMilage ??
    meetingSummary.ClientSubmittedMilage ??
    null
  const travelH =
    meetingSummary.requesterTravelTimeH ??
    meetingSummary.RequesterTravelTimeH ??
    meetingSummary.clientSubmittedTravelTimeH ??
    meetingSummary.ClientSubmittedTravelTimeH ??
    null
  const travelM =
    meetingSummary.requesterTravelTimeM ??
    meetingSummary.RequesterTravelTimeM ??
    meetingSummary.clientSubmittedTravelTimeM ??
    meetingSummary.ClientSubmittedTravelTimeM ??
    null
  const signUrl =
    meetingSummary.reqSignUrl ??
    meetingSummary.ReqSignUrl ??
    meetingSummary.bulkApproverSignUrl ??
    meetingSummary.BulkApproverSignUrl ??
    null
  const remarks =
    meetingSummary.bulkClientRemarks ?? meetingSummary.BulkClientRemarks ?? null

  return {show: true as const, start, end, mileage, travelH, travelM, signUrl, remarks}
}

const DigitalVosSubmittedDetailsPanel: React.FC<{
  meetingSummary: any
  selectedSource: DigitalVosSource | null
  onSelectClient: () => void
  onSelectInterpreter: () => void
}> = ({meetingSummary, selectedSource, onSelectClient, onSelectInterpreter}) => {
  const clientVos = resolveClientSubmittedVosDisplay(meetingSummary)

  const cardClass = (source: DigitalVosSource) =>
    `card border rounded p-3 mb-3 ${selectedSource === source ? 'border-primary border-2' : ''}`

  return (
    <div>
      <h5 className='fw-bold mb-3'>Submitted Details</h5>
      <button
        type='button'
        className={`${cardClass('client')} w-100 text-start`}
        onClick={onSelectClient}
        disabled={!clientVos.show}
        style={{cursor: clientVos.show ? 'pointer' : 'not-allowed'}}
      >
        <div className='fw-bold mb-3'>Client Submitted VOS</div>
        {clientVos.show ? (
          <>
            <span className='d-flex mb-2'>
              <i className='fa-solid fa-clock me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
              <span className='me-2 fw-bold'>Start Time:</span>
              {clientVos.start ? moment(clientVos.start).format('hh:mm A') : '-'}
            </span>
            <span className='d-flex mb-2'>
              <i className='fa-solid fa-clock me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
              <span className='me-2 fw-bold'>End Time:</span>
              {clientVos.end ? moment(clientVos.end).format('hh:mm A') : '-'}
            </span>
            <span className='d-flex mb-2'>
              <i className='fa-solid fa-clock me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
              <span className='me-2 fw-bold'>Duration:</span>
              {formatVosDuration(clientVos.start, clientVos.end)}
            </span>
            <span className='d-flex mb-2'>
              <i className='fa-solid fa-car me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
              <span className='me-2 fw-bold'>Mileage Of Drive (miles):</span>
              {clientVos.mileage != null ? clientVos.mileage : '-'}
            </span>
            <span className='d-flex mb-2'>
              <i className='fa-solid fa-briefcase me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
              <span className='me-2 fw-bold'>Travel Time:</span>
              {formatVosTravelTime(clientVos.travelH, clientVos.travelM)}
            </span>
            {clientVos.remarks ? (
              <div className='mb-2'>
                <span className='fw-bold d-block mb-1'>REMARKS:</span>
                <span className='text-muted small'>{clientVos.remarks}</span>
              </div>
            ) : null}
            <div className='mt-3'>
              <VOSSignatureImage fileUrl={clientVos.signUrl} label='Client Signature' />
            </div>
          </>
        ) : (
          <span className='text-muted small'>Not yet confirmed or disputed</span>
        )}
      </button>
      <button
        type='button'
        className={`${cardClass('interpreter')} w-100 text-start`}
        onClick={onSelectInterpreter}
      >
        <div className='fw-bold mb-3'>Interpreter Submitted VOS</div>
        <span className='d-flex mb-2'>
          <i className='fa-solid fa-clock me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
          <span className='me-2 fw-bold'>Start Time:</span>
          {meetingSummary?.startDate ? moment(meetingSummary.startDate).format('hh:mm A') : '-'}
        </span>
        <span className='d-flex mb-2'>
          <i className='fa-solid fa-clock me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
          <span className='me-2 fw-bold'>End Time:</span>
          {meetingSummary?.endDate ? moment(meetingSummary.endDate).format('hh:mm A') : '-'}
        </span>
        <span className='d-flex mb-2'>
          <i className='fa-solid fa-clock me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
          <span className='me-2 fw-bold'>Duration:</span>
          {formatVosDuration(meetingSummary?.startDate, meetingSummary?.endDate)}
        </span>
        <span className='d-flex mb-2'>
          <i className='fa-solid fa-car me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
          <span className='me-2 fw-bold'>Mileage Of Drive (miles):</span>
          {meetingSummary?.milage != null ? meetingSummary.milage : '-'}
        </span>
        <span className='d-flex mb-2'>
          <i className='fa-solid fa-briefcase me-2 text-muted align-self-center' style={{fontSize: '12px'}} />
          <span className='me-2 fw-bold'>Travel Time:</span>
          {formatVosTravelTime(meetingSummary?.travalTimeH, meetingSummary?.travalTimeM)}
        </span>
        <div className='mt-3'>
          <VOSSignatureImage fileUrl={meetingSummary?.terpSignUrl} label='Interpreter Signature' />
        </div>
      </button>
    </div>
  )
}

const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// Helper function to convert 24-hour format to 12-hour AM/PM format
const formatTimeToAmPm = (timeStr: string): string => {
  if (!timeStr || timeStr.length < 5) return timeStr
  const [hours, minutes] = timeStr.substring(0, 5).split(':').map(Number)
  const hour12 = hours % 12 || 12
  const ampm = hours < 12 ? 'AM' : 'PM'
  return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`
}

const getDurationDisplay = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return '00:00:00'
  const startMinutes = timeToMinutes(startTime)
  let endMinutes = timeToMinutes(endTime)
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60
  }
  const durationMinutes = Math.max(endMinutes - startMinutes, 0)
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
}

const AddOSIDetails: React.FC<ModalProps> = ({
  initialValues,
  fetchAfterSubmitOSISubmmission,
  expenseList,
  setExpenseList,
  isOsiEdit,
  readOnly = false,
  payableHours,
  hourlyRate,
  appointmentDetails,
  meetingSummaryStatus,
  isResubmit = false,
  onRevertApproval,
  onEditWithoutUnlinking,
  isEditWithoutUnlinking = false,
  setIsEditWithoutUnlinking,
  setShowOsiReadOnly,
  meetingSummary,
  botRecordedTimes = null,
  showUpdateAndApprove = false,
  appointmentCodeOverride,
  modalElementId = 'kt_add_osi_details',
  forceDigitalVosEditLayout = false,
  defaultDigitalVosSource,
}) => {
  let {id} = useParams()
  const {currentUser} = useAuth()
  const resolvedAppointmentCode =
    appointmentCodeOverride != null && Number.isInteger(appointmentCodeOverride) && appointmentCodeOverride > 0
      ? appointmentCodeOverride
      : id != null && id !== ''
        ? Number(id)
        : NaN

  // Check if the current user is an INTERPRETER
  const isInterpreterUser = currentUser?.result.userType === 'INTERPRETER'

  const isDigitalVos = Number((initialValues as any)?.vosSubmissionMode) === 1
  const isDigitalVosEditLayout =
    isOsiEdit &&
    !readOnly &&
    !!initialValues?.code &&
    (forceDigitalVosEditLayout ? Boolean(meetingSummary) : isDigitalVos)
  const showVosUploadInLayout = !isDigitalVosEditLayout || forceDigitalVosEditLayout
  const [selectedVosSource, setSelectedVosSource] = useState<DigitalVosSource | null>(null)

  useEffect(() => {
    if (!isDigitalVosEditLayout) {
      setSelectedVosSource(null)
      return
    }
    if (defaultDigitalVosSource === 'client' && meetingSummary) {
      const clientVos = resolveClientSubmittedVosDisplay(meetingSummary)
      setSelectedVosSource(clientVos.show ? 'client' : null)
      return
    }
    if (defaultDigitalVosSource === 'interpreter') {
      setSelectedVosSource('interpreter')
    }
  }, [isDigitalVosEditLayout, initialValues?.code, defaultDigitalVosSource, meetingSummary])

  console.log('AddOSIDetails - initialValues:', initialValues)
  const vosFormInputRef = useRef<HTMLInputElement>(null)
  const expenseFileInputRef = useRef<HTMLInputElement>(null)
  const pendingExpenseRef = useRef<any>(null)
  const signaturePadRef = useRef<SignatureCanvas>(null)
  const [showRevertConfirm, setShowRevertConfirm] = useState(false)
  const [signatureError, setSignatureError] = useState<string | null>(null)
  const [hasInvalidVosFileUpload, setHasInvalidVosFileUpload] = useState(false)
  const [hasInvalidExpenseFileUpload, setHasInvalidExpenseFileUpload] = useState(false)
  const isSystemUser = currentUser?.result?.userType?.toUpperCase() === 'SYSTEM'
  const isApprovedStatus =
    Number(meetingSummaryStatus) === 2 ||
    Number((meetingSummary as any)?.status) === 2 ||
    Number((meetingSummary as any)?.Status) === 2 ||
    Number((initialValues as any)?.status) === 2 ||
    Number((initialValues as any)?.Status) === 2
  const useUpdateAndApprove =
    isSystemUser && isOsiEdit && !!initialValues?.code && showUpdateAndApprove

  const resetVosFileUploadState = () => {
    setHasInvalidVosFileUpload(false)
    setVosRejected(false)
    if (vosFormInputRef.current) vosFormInputRef.current.value = ''
  }

  const resetExpenseFileUploadState = () => {
    setHasInvalidExpenseFileUpload(false)
    if (expenseFileInputRef.current) expenseFileInputRef.current.value = ''
  }

  // VOS OCR: extracted values from file for form fill and dispute detection
  const [vosBlurryOrUnreadable, setVosBlurryOrUnreadable] = useState(false)
  const [ocrExtractedStartDate, setOcrExtractedStartDate] = useState<string | null>(null)
  const [ocrExtractedEndDate, setOcrExtractedEndDate] = useState<string | null>(null)
  const [ocrTotalTravelTime, setOcrTotalTravelTime] = useState<string | null>(null) // e.g. "1:30"
  const [ocrTotalMileage, setOcrTotalMileage] = useState<number | null>(null)
  const [ocrExtractedBreakTimeMinutes, setOcrExtractedBreakTimeMinutes] = useState<number | null>(null)
  const [includeBreakTimeDeduction, setIncludeBreakTimeDeduction] = useState(false)
  const [vosExtracting, setVosExtracting] = useState(false)
  const [vosRejected, setVosRejected] = useState(false)
  const [interpreterSignaturePresent, setInterpreterSignaturePresent] = useState(false)
  const [signInSignaturePresent, setSignInSignaturePresent] = useState(false)
  const [signOutSignaturePresent, setSignOutSignaturePresent] = useState(false)
  const [disputeReasons, setDisputeReasons] = useState<string[]>([])

  const clearVosOcrState = () => {
    setVosBlurryOrUnreadable(false)
    setVosRejected(false)
    setOcrExtractedStartDate(null)
    setOcrExtractedEndDate(null)
    setOcrTotalTravelTime(null)
    setOcrTotalMileage(null)
    setInterpreterSignaturePresent(false)
    setSignInSignaturePresent(false)
    setSignOutSignaturePresent(false)
    setDisputeReasons([])
  }
  const [showTimeVerification, setShowTimeVerification] = useState(false)
  const [timeVerificationDuration, setTimeVerificationDuration] = useState('00:00:00')
  const verifiedSubmitRef = useRef(false)

  // Resize signature canvas when modal is shown so it has correct dimensions and accepts input
  useEffect(() => {
    const modalEl = document.getElementById('kt_add_osi_details')
    if (!modalEl) return
    const onShown = () => {
      setTimeout(() => (signaturePadRef.current as { _resizeCanvas?: () => void })?._resizeCanvas?.(), 100)
    }
    modalEl.addEventListener('shown.bs.modal', onShown)
    return () => modalEl.removeEventListener('shown.bs.modal', onShown)
  }, [])

  // Auto-add adjustment expense when payableHours exists
  useEffect(() => {
    console.log('AddOSIDetails - payableHours:', payableHours, 'hourlyRate:', hourlyRate, 'isOsiEdit:', isOsiEdit)
    if (payableHours && hourlyRate && isOsiEdit) {
      const calculatedAmount = payableHours * hourlyRate
      // console.log('Calculated amount:', calculatedAmount, '= payableHours:', payableHours, '* hourlyRate:', hourlyRate)
      // const adjustmentExpense = {
      //   description: 'Adjustment for Cancellation',
      //   amount: calculatedAmount.toString(),
      //   file: '',
      //   dataUrl: '',
      //   code: 'adjustment-' + Date.now(), // Unique code for this adjustment
      // }
      
      // // Check if adjustment expense already exists
      // const existingAdjustment = expenseList.find((exp: any) => 
      //   exp.description === 'Adjustment for Cancellation' && exp.code?.startsWith('adjustment-')
      // )
      
      // if (!existingAdjustment) {
      //   setExpenseList([...expenseList, adjustmentExpense])
      // }
    }
  }, [payableHours, hourlyRate, isOsiEdit, expenseList, setExpenseList])
  // Reset VOS OCR state when opening for a new submission (no existing code)
  useEffect(() => {
    if (!initialValues?.code) {
      setVosBlurryOrUnreadable(false)
      setVosRejected(false)
      setOcrExtractedStartDate(null)
      setOcrExtractedEndDate(null)
      setOcrTotalTravelTime(null)
      setOcrTotalMileage(null)
      setOcrExtractedBreakTimeMinutes(null)
    }
  }, [initialValues?.code])

  useEffect(() => {
    const customerId = appointmentDetails?.fk_Customer ?? appointmentDetails?.FK_Customer
    if (!customerId) return
    axios
      .get(`${API_URL}/CustomerSettings/break-time-calculation/${customerId}`)
      .then((res) => setIncludeBreakTimeDeduction(!!res.data?.includeBreakTimeInCalculation))
      .catch(() => setIncludeBreakTimeDeduction(false))
  }, [appointmentDetails?.fk_Customer, appointmentDetails?.FK_Customer])

  const isThirdpartyAppointment = appointmentDetails?.communicationTypeIdentification == 'THIRD_PART_PLATFORM';

  const addAppointmentDetailsSchema = Yup.object().shape({
    startTime: Yup.string().required('Required'),
    endTime: Yup.string()
      // .test('is-after-start', 'End time cannot be before start time', function (value) {
      //   const {startTime} = this.parent
      //   if (startTime && value) {
      //     return timeToMinutes(value) > timeToMinutes(startTime)
      //   }
      //   return true
      // })
      .required('Required'),
    travelHours: Yup.number(),
    travelMinutes: Yup.number(),
    isRoundTrip: Yup.boolean(),
    milageOfDrive: Yup.number().min(0, 'Milage cannot be negative'),
    breakTimeMinutes: Yup.number().min(0, 'Break time cannot be negative').nullable(),
    submissionTab: Yup.string().oneOf(['manual', 'digital']).required(),
    vosForm: Yup.mixed()
      .when(['code', 'submissionTab'], {
        is: (code: string, submissionTab: string) =>
          String(code) === '' && submissionTab === 'manual',
        then: (schema) => schema.required('VOS Form is required for new records'),
        otherwise: (schema) => schema.nullable(),
      })
      .test(
        'supported-file-type',
        'Please upload PDF, JPEG or PNG file',
        (value) => isSupportedUploadValue(value)
      ),
  })

  const addAdditionalExpensesSchema = Yup.object().shape({
    description: Yup.string().required('Required'),
    amount: Yup.number().required('Required'),
    file: Yup.mixed()
      .test('file-required', 'Required', function (value) {
        const {description} = this.parent

        if (description === 'Adjustment for Cancellation') {
          return true
        }

        if (currentUser?.result.userType === 'SYSTEM' && isOsiEdit) {
          return true
        }

        return !!value
      })
      .test(
        'supported-file-type',
        'Please upload PDF, JPEG or PNG file',
        (value) => isSupportedUploadValue(value)
      ),
  })

  const onSelectExpenseFile = async (e: any, setFieldValue: any) => {
    const file = e.target.files?.[0]
    if (!file) {
      setFieldValue('file', '')
      setFieldValue('dataUrl', '')
      setHasInvalidExpenseFileUpload(false)
      return
    }

    if (!isSupportedUploadFile(file)) {
      setFieldValue('file', '')
      setFieldValue('dataUrl', '')
      setHasInvalidExpenseFileUpload(true)
      if (expenseFileInputRef.current) expenseFileInputRef.current.value = ''
      toaster('error', 'Please upload PDF, JPEG or PNG file')
      return
    }

    setHasInvalidExpenseFileUpload(false)
    setFieldValue('file', file)
    const reader = new FileReader()
    reader.onload = (ev) => setFieldValue('dataUrl', ev.target?.result ?? '')
    reader.readAsDataURL(file)
  }

  const onSelectVosFormFile = async (e: any, setFieldValue: any) => {
    const file = e.target.files?.[0]
    if (!file) {
      setFieldValue(e.target.name, '')
      setHasInvalidVosFileUpload(false)
      clearVosOcrState()
      return
    }
    if (!isSupportedUploadFile(file)) {
      setFieldValue(e.target.name, '')
      setHasInvalidVosFileUpload(true)
      if (vosFormInputRef.current) vosFormInputRef.current.value = ''
      toaster('error', 'Please upload PDF, JPEG or PNG file')
      return
    }
    setHasInvalidVosFileUpload(false)
    setFieldValue(e.target.name, file)
    clearVosOcrState()
    const reader = new FileReader()
    reader.onload = (ev) => setFieldValue('dataUrl', ev.target?.result ?? '')
    reader.readAsDataURL(file)

    const rejectVosForm = () => {
      setFieldValue(e.target.name, '')
      setFieldValue('dataUrl', '')
      if (vosFormInputRef.current) vosFormInputRef.current.value = ''
      clearVosOcrState()
      setVosRejected(true)
      toaster('error', VOS_UNREADABLE_MESSAGE)
    }

      setVosExtracting(true)
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await axios.post(`${API_URL}/Appoinment/upload-vos-extract`, formData, {
          headers: { Accept: 'application/json' },
        })
        const extResp = res?.data?.result
        const data = extResp?.success && !extResp.error ? extResp.data : null
        if (!data?.arrivalTime || !data?.departureTime) {
          rejectVosForm()
        } else {
          setInterpreterSignaturePresent(data.interpreterSignaturePresent)
          setSignInSignaturePresent(data.signInSignaturePresent)
          setSignOutSignaturePresent(data.signOutSignaturePresent)
          setVosBlurryOrUnreadable(data.blurryFile)
          setFieldValue('startTime', data.arrivalTime)
          if (data.signInDate) {
            setOcrExtractedStartDate(`${data.signInDate} ${data.arrivalTime}`)
          }

          setFieldValue('endTime', data.departureTime)
          if (data.signOutDate) {
            setOcrExtractedEndDate(`${data.signOutDate} ${data.departureTime}`)
          }

          if (data.totalMileage != null) {
            setFieldValue('milageOfDrive', data.totalMileage)
            setOcrTotalMileage(data.totalMileage)
          } else {
            setFieldValue('milageOfDrive', '')
            setOcrTotalMileage(null)
          }

          if (data.breakTimeMinutes != null && data.breakTimeMinutes !== '') {
            const breakMinutes = Number(data.breakTimeMinutes)
            if (Number.isFinite(breakMinutes) && breakMinutes > 0) {
              setOcrExtractedBreakTimeMinutes(breakMinutes)
              if (includeBreakTimeDeduction) {
                setFieldValue('breakTimeMinutes', breakMinutes)
              }
            } else {
              setOcrExtractedBreakTimeMinutes(null)
            }
          } else {
            setOcrExtractedBreakTimeMinutes(null)
          }

          if (data.totalTravelTime) {
            const parts = data.totalTravelTime.split(':')
            if (parts.length === 2) {
              setFieldValue('travelHours', parseInt(parts[0]) || 0)
              setFieldValue('travelMinutes', parseInt(parts[1]) || 0)
            } else {
              setFieldValue('travelHours', '')
              setFieldValue('travelMinutes', parseInt(data.totalTravelTime) || 0)
            }
            setOcrTotalTravelTime(data.totalTravelTime)
          } else {
            setFieldValue('travelHours', '')
            setFieldValue('travelMinutes', '')
            setOcrTotalTravelTime(null)
          }
        }
      } catch {
        toaster('warning', VOS_READ_FALLBACK_MESSAGE)
        setVosBlurryOrUnreadable(true)
      } finally {
        setVosExtracting(false)
      }
  }

  return (
    <>
    <div className='modal fade' tabIndex={-1} id={modalElementId}>
      <div className={`modal-dialog ${isDigitalVosEditLayout ? 'modal-xl' : 'modal-md'}`}>
        <Formik
          key={initialValues.code || 'new'}
          enableReinitialize
          initialValues={{
            addStaffMember: false,
            staffMemberName: '',
            staffMemberEmail: '',
            ...initialValues,
            submissionTab: isDigitalVos ? 'digital' : 'manual',
          }}
          validationSchema={addAppointmentDetailsSchema}
          onSubmit={async (values, {setSubmitting, resetForm}) => {
            if (!verifiedSubmitRef.current) {
              setTimeVerificationDuration(getDurationDisplay(values.startTime, values.endTime))
              setShowTimeVerification(true)
              setSubmitting(false)
              return
            }
            verifiedSubmitRef.current = false
            setSubmitting(true)
            setSignatureError(null)
            try {
              const appointmentCode = resolvedAppointmentCode
              if (!Number.isInteger(appointmentCode) || appointmentCode <= 0) {
                toaster('error', 'Invalid appointment. Please open from an appointment page.')
                setSubmitting(false)
                return
              }

              const submissionTab =
                isDigitalVos && values.submissionTab === 'digital' ? 'digital' : 'manual'
              const isExistingOsiEdit = isOsiEdit && !!initialValues?.code
              if (!readOnly && submissionTab === 'digital' && !isExistingOsiEdit) {
                const isEmpty = (signaturePadRef.current as { isEmpty?: () => boolean } | null)?.isEmpty?.()
                if (isEmpty) {
                  setSignatureError('Digital signature is required.')
                  toaster('error', 'Digital signature is required.')
                  setSubmitting(false)
                  return
                }
              }
              setSignatureError(null)

              const formData = new FormData()
              // Use date from initialValues and time from form
              // CRITICAL: Always use appointment date, never fall back to "today"
              let date: any
              
              if (appointmentDetails?.startTime) {
                date = moment(appointmentDetails.startTime).format('YYYY-MM-DD HH:mm:ss')
              } else if (initialValues?.startDateTime && initialValues.startDateTime.trim()) {
                date = moment(initialValues.startDateTime).format('YYYY-MM-DD HH:mm:ss')
              }
              // Only use today as last resort
              else {
                date = moment().utcOffset(-4).format('YYYY-MM-DD HH:mm:ss'); // -4 for Eastern Time (EDT)
              }

              const startTime = values?.startTime || null;
              const endTime = values?.endTime || null;

              // fallback: if no times are provided, use current time
              let startDateTime = startTime
                ? moment(`${date.split(' ')[0]} ${startTime}`, 'YYYY-MM-DD HH:mm')
                : moment().utcOffset(-4);

              let endDateTime = endTime
                ? moment(`${date.split(' ')[0]} ${endTime}`, 'YYYY-MM-DD HH:mm')
                : moment(startDateTime).add(1, 'hours'); // default 1-hour duration

              if (endDateTime.isBefore(startDateTime)) {
                endDateTime.add(1, 'days');
              }

              // ISO 8601 local format for reliable server DateTime binding
              formData.append('StartDate', startDateTime.format('YYYY-MM-DDTHH:mm:ss'));
              formData.append('EndDate', endDateTime.format('YYYY-MM-DDTHH:mm:ss'));

              const reasons: string[] = []
              if (submissionTab === 'manual') {
                if (!values.startTime || !values.startTime.trim()) {
                  reasons.push('Start time is required')
                }
                if (!values.endTime || !values.endTime.trim()) {
                  reasons.push('End time is required')
                }
                if (!interpreterSignaturePresent) {
                  reasons.push('Interpreter signature missing')
                }
                if (!signInSignaturePresent) {
                  reasons.push('Sign In signature missing')
                }
                if (!signOutSignaturePresent) {
                  reasons.push('Sign Out signature missing')
                }
                if (ocrExtractedStartDate) {
                  const extractedStartTime = ocrExtractedStartDate.split(' ')[1] || ''
                  if (values.startTime && extractedStartTime && values.startTime !== extractedStartTime) {
                    reasons.push(`Start time mismatch: OCR ${extractedStartTime} → Form ${values.startTime}`)
                  }
                }
                if (ocrExtractedEndDate) {
                  const extractedEndTime = ocrExtractedEndDate.split(' ')[1] || ''
                  if (values.endTime && extractedEndTime && values.endTime !== extractedEndTime) {
                    reasons.push(`End time mismatch: OCR ${extractedEndTime} → Form ${values.endTime}`)
                  }
                }
                const formMileage =
                  values.milageOfDrive != null && values.milageOfDrive !== '' ? Number(values.milageOfDrive) : null
                if (ocrTotalMileage != null) {
                  if (formMileage == null) {
                    reasons.push(`Mileage missing: expected ${ocrTotalMileage} mi`)
                  } else if (Math.abs(formMileage - ocrTotalMileage) > 0.01) {
                    reasons.push(`Mileage mismatch: ${ocrTotalMileage} → ${formMileage} mi`)
                  }
                } else if (formMileage != null && formMileage !== 0) {
                  reasons.push(`Mileage added: ${formMileage} mi`)
                }
                const formTravelH = values.travelHours != null && values.travelHours !== '' ? Number(values.travelHours) : null
                const formTravelM = values.travelMinutes != null && values.travelMinutes !== '' ? Number(values.travelMinutes) : null
                const hasFormTravel = (formTravelH != null && formTravelH > 0) || (formTravelM != null && formTravelM > 0)
                if (ocrTotalTravelTime) {
                  const ocrParts = ocrTotalTravelTime.trim().split(/[:\s]+/)
                  const ocrH = ocrParts[0] ? parseInt(ocrParts[0], 10) : 0
                  const ocrM = ocrParts[1] ? parseInt(ocrParts[1], 10) : 0
                  if (!hasFormTravel) {
                    reasons.push(`Travel time missing: expected ${ocrH}h ${ocrM}m`)
                  } else if (ocrH !== (formTravelH ?? 0) || ocrM !== (formTravelM ?? 0)) {
                    reasons.push(`Travel time mismatch: ${ocrH}h ${ocrM}m → ${formTravelH ?? 0}h ${formTravelM ?? 0}m`)
                  }
                } else if (hasFormTravel) {
                  reasons.push(`Travel time added: ${formTravelH ?? 0}h ${formTravelM ?? 0}m`)
                }
                const formBreak =
                  values.breakTimeMinutes != null && values.breakTimeMinutes !== ''
                    ? Number(values.breakTimeMinutes)
                    : null
                const deductionEnabled =
                  includeBreakTimeDeduction || !!meetingSummary?.isBreakTimeDeductionEnabled
                if (deductionEnabled) {
                  if (formBreak == null && ocrExtractedBreakTimeMinutes == null) {
                    reasons.push('Break time missing')
                  } else if (
                    ocrExtractedBreakTimeMinutes != null &&
                    ocrExtractedBreakTimeMinutes > 0 &&
                    formBreak != null &&
                    formBreak !== ocrExtractedBreakTimeMinutes
                  ) {
                    reasons.push(
                      `Break time mismatch: ${ocrExtractedBreakTimeMinutes} → ${formBreak} mins`
                    )
                  }
                }
              }

              setDisputeReasons(reasons)
              const isDispute = submissionTab === 'digital' ? false : reasons.length > 0
             
              
              formData.append('IsDispute', isDispute ? 'true' : 'false')
              if (isDispute && reasons.length) {
                formData.append('DisputeReason', reasons.join('; '))
              }

              formData.append('Milage', values?.milageOfDrive === '' ? 0 : values?.milageOfDrive ?? 0)
              formData.append('IsRoundTrip', values?.isRoundTrip ? 'true' : 'false')
              const travelH = values?.travelHours != null && values.travelHours !== '' ? Number(values.travelHours) : 0
              const travelM = values?.travelMinutes != null && values.travelMinutes !== '' ? Number(values.travelMinutes) : 0
              formData.append('TravalTimeH', String(travelH))
              formData.append('TravalTimeM', String(travelM))
              if (values.breakTimeMinutes !== '' && values.breakTimeMinutes != null) {
                formData.append('BreakTimeMinutes', String(values.breakTimeMinutes))
              }
              formData.append('FK_AppointmentCode', String(appointmentCode))
              formData.append('IsTimeChargeableForInterpreter', values.isTimeChargeableForInterpreter.toString());
              formData.append('IsTimeChargeableForClient', values.isTimeChargeableForClient.toString());
              
              formData.append('IsMileagePayableForInterpreter', isThirdpartyAppointment ? false : (initialValues?.code ? values.isMileagePayableForInterpreter : true));
              formData.append('IsMileageChargeableForClient', isThirdpartyAppointment ? false : (initialValues?.code ? values.isMileageChargeableForClient : true));
              formData.append('IsTravelTimePayableForInterpreter', isThirdpartyAppointment ? false : (initialValues?.code ? values.isTravelTimePayableForInterpreter : true));
              formData.append('IsTravelTimeChargeableForClient', isThirdpartyAppointment ? false : (initialValues?.code ? values.isTravelTimeChargeableForClient : true));
             
              if (
                (submissionTab === 'manual' || forceDigitalVosEditLayout) &&
                values?.vosForm instanceof File
              ) {
                formData.append('OSIDocumentList', values.vosForm)
              }
              if (submissionTab === 'digital' && !isExistingOsiEdit && values?.addStaffMember) {
                if (values?.staffMemberName != null) formData.append('StaffMemberName', String(values.staffMemberName).trim())
                if (values?.staffMemberEmail != null) formData.append('StaffMemberEmail', String(values.staffMemberEmail).trim())
              }
              if (submissionTab === 'digital' && !isExistingOsiEdit) {
                // SignedVia is intentionally not sent here; it is set only when the requester signs the VOS confirm/dispute form.
                const signatureDataUrl = signaturePadRef.current
                  ? signaturePadRef.current.getTrimmedCanvas()?.toDataURL('image/png')
                  : null
                if (signatureDataUrl) {
                  formData.append('SignatureBase64', signatureDataUrl)
                }
              }
              let expensesToSubmit = [...expenseList]
              const pendingExpense = pendingExpenseRef.current
              const pendingDescription = String(pendingExpense?.description ?? '').trim()
              const pendingAmount = pendingExpense?.amount
              const hasPendingExpense =
                pendingDescription !== '' &&
                pendingAmount !== '' &&
                pendingAmount != null

              if (hasPendingExpense) {
                try {
                  await addAdditionalExpensesSchema.validate(pendingExpense)
                  expensesToSubmit = [...expensesToSubmit, pendingExpense]
                } catch {
                  toaster('error', 'Please complete the Additional Expenses fields or click Add.')
                  setSubmitting(false)
                  return
                }
              }

              expensesToSubmit.forEach((_item: any, index: number) => {
                formData.append(`OSIExpensesList[${index}][Code]`, _item?.code ?? '')
                formData.append(`OSIExpensesList[${index}][Amount]`, String(_item?.amount ?? ''))
                formData.append(`OSIExpensesList[${index}][Description]`, _item?.description ?? '')
                if (_item?.file instanceof File) {
                  formData.append(`OSIExpensesList[${index}].File`, _item.file)
                }
                formData.append(`OSIExpensesList[${index}][IsExpensesPayableForInterpreter]`, typeof _item?.isExpensesPayableForInterpreter === 'boolean' ? _item.isExpensesPayableForInterpreter : true)
                formData.append(`OSIExpensesList[${index}][IsExpensesChargeableForClient]`, typeof _item?.isExpensesChargeableForClient === 'boolean' ? _item.isExpensesChargeableForClient : true)
              })

              if (isExistingOsiEdit) {
                formData.append('Code', String(initialValues.code))
                if (useUpdateAndApprove) {
                  formData.append('ApproveAfterUpdate', 'true')
                }
                if (isEditWithoutUnlinking) {
                  formData.append('IsEditWithoutUnlinking', 'true')
                }
                if (isSystemUser) {
                  if (
                    values.interpreterRateHour !== '' &&
                    values.interpreterRateHour != null &&
                    !Number.isNaN(Number(values.interpreterRateHour))
                  ) {
                    formData.append('InterpreterUpdatedRate', String(values.interpreterRateHour))
                  }
                  if (
                    values.clientRateHour !== '' &&
                    values.clientRateHour != null &&
                    !Number.isNaN(Number(values.clientRateHour))
                  ) {
                    formData.append('CLientRate', String(values.clientRateHour))
                  }
                }
              }

              const submitUrl = isExistingOsiEdit
                ? `${API_URL}/Appoinment/osi-submission-appointment`
                : submissionTab === 'digital'
                  ? `${API_URL}/Appoinment/digital-vos-submission-appointment`
                  : `${API_URL}/Appoinment/manual-vos-submission-appointment`

              const result: any = isExistingOsiEdit
                ? await axios.put(submitUrl, formData, {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                      Accept: 'application/json',
                    },
                  })
                : await axios.post(submitUrl, formData, {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                      Accept: 'application/json',
                    },
                  })

              if (result?.data?.status == 'S') {
                toaster('success', result?.data?.text ?? 'Successfully Inserted')
                setShowTimeVerification(false)
                setIsEditWithoutUnlinking?.(false)
                const modal = document.getElementById(modalElementId)
                if (modal) {
                  const modalInstance = Modal.getInstance(modal)
                  if (modalInstance) modalInstance.hide()
                }
                if (isExistingOsiEdit) {
                  fetchAfterSubmitOSISubmmission({
                    isEdit: true,
                    values,
                    expenses: expensesToSubmit,
                    startDateTime,
                    endDateTime,
                  })
                  resetVosFileUploadState()
                  resetExpenseFileUploadState()
                  resetForm({values})
                } else {
                  fetchAfterSubmitOSISubmmission()
                  resetVosFileUploadState()
                  resetExpenseFileUploadState()
                  resetForm()
                }
              } else if (result?.data?.status == 'E') {
                toaster('error', result?.data?.text ?? 'Error')
              }
            } catch (ex: any) {
              const errorMessage = ex?.response?.data?.text || ex?.response?.data?.message || 'Server Error'
              toaster('error', errorMessage)
              console.error(ex)
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({
            isSubmitting,
            handleChange,
            handleBlur,
            setFieldTouched,
            setFieldValue,
            handleSubmit,
            resetForm,
            values,
            errors,
            touched,
            isValid,
            dirty,
            ...formik
          }) => {
            const applyClientVosToForm = () => {
              if (!meetingSummary) return
              const clientVos = resolveClientSubmittedVosDisplay(meetingSummary)
              if (!clientVos.show) return
              const start = clientVos.start
              const end = clientVos.end
              if (start) setFieldValue('startTime', moment(start).format('HH:mm'))
              if (end) setFieldValue('endTime', moment(end).format('HH:mm'))
              setFieldValue(
                'milageOfDrive',
                clientVos.mileage != null ? clientVos.mileage : ''
              )
              setFieldValue(
                'isRoundTrip',
                Boolean(
                  meetingSummary.requesterIsRoundTrip ??
                    meetingSummary.RequesterIsRoundTrip ??
                    meetingSummary.isRoundTrip
                )
              )
              const travelH = clientVos.travelH ?? 0
              const travelM = clientVos.travelM ?? 0
              setFieldValue('travelHours', travelH)
              setFieldValue('travelMinutes', travelM)
              const clientBreak =
                readOsiBreakTimeMinutes(meetingSummary as Record<string, unknown>) ??
                readOsiBreakTimeMinutes({
                  breakTimeMinutes:
                    meetingSummary.requesterBreakTimeMinutes ??
                    meetingSummary.RequesterBreakTimeMinutes,
                } as Record<string, unknown>)
              setFieldValue('breakTimeMinutes', clientBreak != null ? clientBreak : '')
              setSelectedVosSource('client')
            }

            const applyInterpreterVosToForm = () => {
              if (!meetingSummary) return
              if (meetingSummary.startDate) {
                setFieldValue('startTime', moment(meetingSummary.startDate).format('HH:mm'))
              }
              if (meetingSummary.endDate) {
                setFieldValue('endTime', moment(meetingSummary.endDate).format('HH:mm'))
              }
              setFieldValue(
                'milageOfDrive',
                meetingSummary.milage != null ? meetingSummary.milage : ''
              )
              setFieldValue('isRoundTrip', Boolean(meetingSummary.isRoundTrip))
              setFieldValue('travelHours', meetingSummary.travalTimeH ?? 0)
              setFieldValue('travelMinutes', meetingSummary.travalTimeM ?? 0)
              const interpreterBreak = readOsiBreakTimeMinutes(
                meetingSummary as Record<string, unknown>
              )
              setFieldValue(
                'breakTimeMinutes',
                interpreterBreak != null ? interpreterBreak : ''
              )
              setSelectedVosSource('interpreter')
            }

            return (
            <div role='form'>
              <div className='modal-content'>
                <div className='modal-header py-2'>
                  <h4 className='modal-title'>
                    {isDigitalVosEditLayout
                      ? 'Edit VOS Details'
                      : isResubmit
                        ? 'Resubmit'
                        : !initialValues?.code
                          ? 'Add'
                          : 'Edit'}{' '}
                    {!isDigitalVosEditLayout && 'Appoinment Details'}
                  </h4>
                  <div
                    className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                    data-bs-dismiss='modal'
                    aria-label='Close'
                    onClick={() => {
                      verifiedSubmitRef.current = false
                      setShowTimeVerification(false)
                      resetVosFileUploadState()
                      resetExpenseFileUploadState()
                      resetForm()
                    }}
                  >
                    <KTSVG
                      path='/media/icons/duotune/arrows/arr061.svg'
                      className='svg-icon svg-icon-2x'
                    />
                  </div>
                </div>

                <div className='modal-body' style={{maxHeight: '80vh', overflowY: 'scroll'}}>
                  <div className='row g-4 g-xl-6'>
                    <div className={isDigitalVosEditLayout ? 'col-lg-6 col-12' : 'col-sm-12 col-md-12 col-lg-12'}>
                      <div className=''>
                        <div className='row g-4 g-xl-6'>
                          <div className='col-sm-12 col-md-12 '>
                            <div className='row g-8 mb-0'>
                              <div className='col-sm-12 col-md-12 col-lg-12'>
                                <div className='col-md-12'>
                                  <div className='col-md-12'>
                                    {readOnly && Number((initialValues as any)?.vosSubmissionMode) === 1 ? (
                                      <div className='mb-3 small text-muted'>
                                        Digital VOS submission — interpreter signature is stored securely.
                                      </div>
                                    ) : null}
                                    {showVosUploadInLayout &&
                                    ((!readOnly &&
                                      (values.submissionTab === 'manual' || forceDigitalVosEditLayout)) ||
                                      (readOnly && Number((initialValues as any)?.vosSubmissionMode) !== 1)) ? (
                                    <div className='mb-3'>
                                      <label
                                        htmlFor='vosForm'
                                        className={`form-label fs-7 mb-1${!readOnly && values.submissionTab === 'manual' && !initialValues?.code ? ' required' : ''}`}
                                      >
                                        Upload VOS Form
                                      </label>
                                      <input
                                        type='file'
                                        accept={SUPPORTED_UPLOAD_ACCEPT}
                                        className='form-control form-control-white form-select-sm custom-input-height'
                                        placeholder='Select File'
                                        onChange={(e) => {
                                          if (
                                            !readOnly &&
                                            (values.submissionTab === 'manual' || forceDigitalVosEditLayout)
                                          ) {
                                            onSelectVosFormFile(e, setFieldValue)
                                            setFieldTouched('vosForm', true, true)
                                          }
                                        }}
                                        name='vosForm'
                                        id='vosForm'
                                        onBlur={handleBlur}
                                        ref={vosFormInputRef}
                                        disabled={readOnly || vosExtracting}
                                      />
                                      {vosExtracting && (
                                        <div className='d-flex align-items-center gap-2 mt-2 text-primary'>
                                          <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                                          <span className='small'>Reading VOS form…</span>
                                        </div>
                                      )}
                                      {vosRejected && (
                                        <div className='text-danger small mt-1' role='alert'>
                                          {VOS_UNREADABLE_MESSAGE}
                                        </div>
                                      )}
                                      {vosBlurryOrUnreadable && (
                                        <div className='text-warning small mt-1'>
                                          Some fields could not be read from the VOS form. Please verify start and end times. You can still submit.
                                        </div>
                                      )}
                                      {hasInvalidVosFileUpload && (
                                        <div className='fv-plugins-message-container'>
                                          <div className='fv-help-block'>
                                            <span role='alert'>Please upload PDF, JPEG or PNG file</span>
                                          </div>
                                        </div>
                                      )}
                                      {touched && errors.vosForm && values.submissionTab === 'manual' && !initialValues?.code && (
                                        <div className='fv-plugins-message-container'>
                                          <div className='fv-help-block'>
                                            <span role='alert'>{String(errors.vosForm)}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    ) : null}
                                <div className='row d-flex mb-3'>
                                  <div className='col-md-6'>
                                    <div className=''>
                                      <label
                                        htmlFor='exampleFormControlInput1'
                                        className='required form-label fs-7 mb-1'
                                      >
                                        Start Time
                                      </label>
                                      <input
                                        id='startTime'
                                        name='startTime'
                                        type='time'
                                        className='form-control form-control-white form-select-sm custom-input-height'
                                        placeholder='Start Time'
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.startTime}
                                        disabled={readOnly}
                                      />
                                      {errors.startTime && touched?.startTime && (
                                        <div className='fv-plugins-message-container'>
                                          <div className='fv-help-block'>
                                            <span role='alert'>{errors.startTime.toString()}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className='col-md-6 '>
                                    <div className=''>
                                      <label
                                        htmlFor='exampleFormControlInput1'
                                        className='required form-label fs-7 mb-1'
                                      >
                                        End Time
                                      </label>

                                      <input
                                        id='endTime'
                                        name='endTime'
                                        type='time'
                                        className='form-control form-control-white form-select-sm custom-input-height'
                                        placeholder='End Time'
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.endTime}
                                        disabled={readOnly}
                                      />
                                      {errors.endTime && touched?.endTime && (
                                        <div className='fv-plugins-message-container'>
                                          <div className='fv-help-block'>
                                            <span role='alert'>{errors.endTime.toString()}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className='col-md-6'>
                                    <div>
                                      <label htmlFor='breakTimeMinutes' className='form-label fs-7 mb-1'>
                                        Break Time (mins)
                                      </label>
                                      <input
                                        id='breakTimeMinutes'
                                        name='breakTimeMinutes'
                                        type='number'
                                        min={0}
                                        className='form-control form-control-white form-select-sm custom-input-height'
                                        placeholder='Break Time (mins)'
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.breakTimeMinutes ?? ''}
                                        disabled={readOnly}
                                      />
                                    </div>
                                  </div>
                                  {!readOnly && botRecordedTimes?.start && (
                                    <div className='col-12 mt-2'>
                                      <span className='d-flex text-muted' style={{fontSize: '12px'}}>
                                        <i
                                          className='fa-solid fa-robot me-2 align-self-center'
                                          style={{fontSize: '12px'}}
                                        />
                                        Bot recorded interpreter time: {botRecordedTimes.start} –{' '}
                                        {botRecordedTimes.end ?? 'in progress'}
                                        {botRecordedTimes.timeZoneName
                                          ? ` (${botRecordedTimes.timeZoneName})`
                                          : ''}
                                      </span>
                                    </div>
                                  )}
                                 {readOnly || (isOsiEdit && initialValues?.code) ? <div className='row mt-2'>
                                    <div className='col-md-6'>
                                      <div className='form-check'>
                                        {isEditWithoutUnlinking ? (
                                          <OverlayTrigger
                                            placement='top'
                                            overlay={<Tooltip id='tooltip-pay-interp'>Unlink the invoice</Tooltip>}
                                          >
                                            <span className='d-inline-block'>
                                              <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='isTimeChargeableForInterpreter'
                                                name='isTimeChargeableForInterpreter'
                                                checked={values.isTimeChargeableForInterpreter}
                                                disabled={true}
                                                style={{cursor: 'not-allowed', pointerEvents: 'none'}}
                                              />
                                            </span>
                                          </OverlayTrigger>
                                        ) : (
                                          <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='isTimeChargeableForInterpreter'
                                            name='isTimeChargeableForInterpreter'
                                            checked={values.isTimeChargeableForInterpreter}
                                            onChange={handleChange}
                                            disabled={readOnly || isInterpreterUser}
                                          />
                                        )}
                                        <label className='form-check-label' htmlFor='isTimeChargeableForInterpreter'>
                                          Pay Interpreter
                                        </label>
                                      </div>
                                    </div>
                                    <div className='col-md-6 '>
                                      <div className='form-check'>
                                        {isEditWithoutUnlinking ? (
                                          <OverlayTrigger
                                            placement='top'
                                            overlay={<Tooltip id='tooltip-charge-client'>Unlink the invoice</Tooltip>}
                                          >
                                            <span className='d-inline-block'>
                                              <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='isTimeChargeableForClient'
                                                name='isTimeChargeableForClient'
                                                checked={values.isTimeChargeableForClient}
                                                disabled={true}
                                                style={{cursor: 'not-allowed', pointerEvents: 'none'}}
                                              />
                                            </span>
                                          </OverlayTrigger>
                                        ) : (
                                          <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='isTimeChargeableForClient'
                                            name='isTimeChargeableForClient'
                                            checked={values.isTimeChargeableForClient}
                                            onChange={handleChange}
                                            disabled={readOnly || isInterpreterUser}
                                          />
                                        )}
                                        <label className='form-check-label' htmlFor='isTimeChargeableForClient'>
                                          Charge Client
                                        </label>
                                      </div>
                                    </div>
                                </div> : null}
                                </div>
                                {
                                  appointmentDetails?.communicationTypeIdentification === 'ON_SITE' && (<div className='row d-flex'>
                                    <div className='col-md-6'>
                                      <div className=''>
                                        <label
                                          htmlFor='exampleFormControlInput1'
                                          className=' form-label fs-7 mb-1'
                                        >
                                          Milage Of Drive(miles)
                                        </label>
                                        <input
                                          id='milageOfDrive'
                                          name='milageOfDrive'
                                          type='number'
                                          className='form-control form-control-white form-select-sm custom-input-height'
                                          placeholder='Milage Of Drive'
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.milageOfDrive}
                                          disabled={readOnly}
                                        />
                                        {errors.milageOfDrive && touched?.milageOfDrive && (
                                          <div className='fv-plugins-message-container'>
                                            <div className='fv-help-block'>
                                              <span role='alert'>
                                                {errors.milageOfDrive.toString()}
                                              </span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className='col-md-6 d-flex align-items-center'>
                                      <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                        <input
                                          className='form-check-input'
                                          type='checkbox'
                                          id='isRoundTrip'
                                          name='isRoundTrip'
                                          checked={values.isRoundTrip}
                                          onChange={(e) => {
                                            if (!readOnly) setFieldValue('isRoundTrip', e.target.checked)
                                          }}
                                          onBlur={handleBlur}
                                          disabled={readOnly}
                                        />
                                        <label className='form-check-label' htmlFor='flexRadioLg'>
                                          Round-trip{' '}
                                          <i
                                            className='bi bi-info-circle text-info'
                                            data-bs-toggle='popover'
                                            data-bs-custom-class='popover-inverse'
                                            data-bs-placement='top'
                                            title='When selected, distance from interpreter address to appointment location will be automatically doubled.'
                                          ></i>
                                        </label>
                                      </div>
                                    </div>
                                    {readOnly || (isOsiEdit && initialValues?.code) ? <div className='row mt-2'>
                                      <div className='col-md-6'>
                                        <div className='form-check'>
                                          <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='isMileagePayableForInterpreter'
                                            name='isMileagePayableForInterpreter'
                                            checked={values.isMileagePayableForInterpreter}
                                            onChange={handleChange}
                                            disabled={readOnly || isInterpreterUser}
                                          />
                                          <label className='form-check-label' htmlFor='isMileagePayableForInterpreter'>
                                            Pay Interpreter
                                          </label>
                                        </div>
                                      </div>
                                      <div className='col-md-6'>
                                        <div className='form-check'>
                                          <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='isMileageChargeableForClient'
                                            name='isMileageChargeableForClient'
                                            checked={values.isMileageChargeableForClient}
                                            onChange={handleChange}
                                            disabled={readOnly || isInterpreterUser}
                                          />
                                          <label className='form-check-label' htmlFor='isMileageChargeableForClient'>
                                            Charge Client
                                          </label>
                                        </div>
                                      </div>
                                    </div> : null}
                                  </div>)
                                }
                                {appointmentDetails?.communicationTypeIdentification === 'ON_SITE' && (<div className='row d-flex  mt-3'>
                                  <div className='col-md-12 w-100'>
                                    <div className='mb-3'>
                                      <label
                                        htmlFor='exampleFormControlInput1'
                                        className=' form-label fs-7 mb-1'
                                      >
                                        Travel Time
                                      </label>
                                      <div className='input-group input-group-sm'>
                                        <div className='d-flex w-auto' style={{ flex: 1 }}>
                                          <Select
                                            {...formik.getFieldProps('travelHours')}
                                            className='react-select-styled react-select-solid react-select-sm flex-grow-1'
                                            classNamePrefix='react-select'
                                            options={HoursOptions}
                                            placeholder='Select Hours'
                                            styles={{
                                              control: (provided: any) => ({
                                                ...provided,
                                                width: '100%',
                                                border: '1px solid #e4e6ef',
                                                borderRadius: '4px 0 0 4px',
                                              }),
                                            }}
                                            value={{
                                              value: values.travelHours,
                                              label:
                                                HoursOptions.find(
                                                  (x: any) => x.value === values.travelHours
                                                )?.label || 'Select Hours',
                                            }}
                                            onChange={(e: any) => {
                                              if (!readOnly) setFieldValue('travelHours', e?.value)
                                            }}
                                            onBlur={(e: any) => setFieldTouched('travelHours', true)}
                                            isDisabled={readOnly}
                                          />
                                        </div>

                                        <span className='input-group-text' style={{ flex: 1 }}>
                                          Hours
                                        </span>
                                        <div className='d-flex w-auto' style={{ flex: 1 }}>
                                          <Select
                                            {...formik.getFieldProps('travelMinutes')}
                                            className='react-select-styled react-select-solid react-select-sm flex-grow-1'
                                            classNamePrefix='react-select'
                                            options={MinutesOptions}
                                            placeholder='Select Minutes'
                                            styles={{
                                              control: (provided: any) => ({
                                                ...provided,
                                                width: '100%',
                                                border: '1px solid #e4e6ef',
                                                borderRadius: '0',
                                              }),
                                            }}
                                            value={{
                                              value: values.travelMinutes,
                                              label:
                                                MinutesOptions.find(
                                                  (x: any) => x.value === values.travelMinutes
                                                )?.label || 'Select Minutes',
                                            }}
                                            onChange={(e: any) => {
                                              if (!readOnly) setFieldValue('travelMinutes', e?.value)
                                            }}
                                            onBlur={(e: any) => setFieldTouched('travelMinutes', true)}
                                            isDisabled={readOnly}
                                          />
                                        </div>

                                        <span className='input-group-text' style={{ flex: 1 }}>
                                          Minutes
                                        </span>
                                      </div>
                                      {errors.travelHours && touched?.travelHours && (
                                        <div className='fv-plugins-message-container'>
                                          <div className='fv-help-block'>
                                            {/* <span role='alert'>{errors.travelHours}</span> */}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {readOnly || (isOsiEdit && initialValues?.code) ? <div className='row mt-2'>
                                    <div className='col-md-6'>
                                      <div className='form-check'>
                                        <input
                                          className='form-check-input'
                                          type='checkbox'
                                          id='isTravelTimePayableForInterpreter'
                                          name='isTravelTimePayableForInterpreter'
                                          checked={values.isTravelTimePayableForInterpreter}
                                          onChange={handleChange}
                                          disabled={readOnly || isInterpreterUser}
                                        />
                                        <label className='form-check-label' htmlFor='isTravelTimePayableForInterpreter'>
                                          Pay Interpreter
                                        </label>
                                      </div>
                                    </div>
                                    <div className='col-md-6'>
                                      <div className='form-check'>
                                        <input
                                          className='form-check-input'
                                          type='checkbox'
                                          id='isTravelTimeChargeableForClient'
                                          name='isTravelTimeChargeableForClient'
                                          checked={values.isTravelTimeChargeableForClient}
                                          onChange={handleChange}
                                          disabled={readOnly || isInterpreterUser}
                                        />
                                        <label className='form-check-label' htmlFor='isTravelTimeChargeableForClient'>
                                          Charge Client
                                        </label>
                                      </div>
                                    </div>
                                  </div> : null}
                                </div>)}
                                {isSystemUser &&
                                  isOsiEdit &&
                                  !!initialValues?.code &&
                                  !readOnly && (
                                    <div className='row d-flex mt-3'>
                                      <div className='col-md-6'>
                                        <label
                                          htmlFor='interpreterRateHour'
                                          className='form-label fs-7 mb-1'
                                        >
                                          Interpreter Rate/ Hour
                                        </label>
                                        <div className='input-group input-group-sm'>
                                          <span className='input-group-text'>$</span>
                                          <input
                                            id='interpreterRateHour'
                                            name='interpreterRateHour'
                                            type='number'
                                            min={0}
                                            step='any'
                                            className='form-control form-control-white form-select-sm custom-input-height'
                                            placeholder='Rate'
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.interpreterRateHour ?? ''}
                                          />
                                        </div>
                                      </div>
                                      <div className='col-md-6'>
                                        <label htmlFor='clientRateHour' className='form-label fs-7 mb-1'>
                                          Client Rate/ Hour
                                        </label>
                                        <div className='input-group input-group-sm'>
                                          <span className='input-group-text'>$</span>
                                          <input
                                            id='clientRateHour'
                                            name='clientRateHour'
                                            type='number'
                                            min={0}
                                            step='any'
                                            className='form-control form-control-white form-select-sm custom-input-height'
                                            placeholder='Rate'
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.clientRateHour ?? ''}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                {!readOnly && (
                                  <Formik
                                    enableReinitialize
                                    initialValues={{
                                      description: payableHours ? 'Adjustment for Cancellation' : '',
                                      amount: payableHours && hourlyRate ? (payableHours * hourlyRate).toString() : '',
                                      file: '',
                                      dataUrl: '',
                                      code: '',
                                      isExpensesPayableForInterpreter: true,
                                      isExpensesChargeableForClient: true,
                                    }}
                                    validationSchema={addAdditionalExpensesSchema}
                                    onSubmit={async (values, {setSubmitting, resetForm}) => {
                                      setSubmitting(true)
                                      try {
                                        setExpenseList([...expenseList, values])
                                      } catch (ex) {
                                        toaster('error', 'Server Error')
                                        console.error(ex)
                                      } finally {
                                        setSubmitting(true)
                                        resetForm()
                                        resetExpenseFileUploadState()
                                      }
                                    }}
                                  >
                                    {({
                                      isSubmitting,
                                      handleChange,
                                      handleBlur,
                                      setFieldTouched,
                                      setFieldValue,
                                      handleSubmit,
                                      resetForm,
                                      values,
                                      errors,
                                      touched,
                                      isValid,
                                      dirty,
                                      ...formik
                                    }) => {
                                      pendingExpenseRef.current = values
                                      return (
                                      <Form>
                                        <div className='row g-4 g-xl-6 border border-dashed border-gray-400 pb-5 rounded mt-5'>
                                          <h4 className='text-gray-600 text-center bg-secondary p-2 mt-0 rounded'>
                                            Additional Expenses
                                          </h4>
                                          <div className='row d-flex'>
                                            <div className='col-md-6'>
                                              <div className='mb-3'>
                                                <label
                                                  htmlFor='exampleFormControlInput1'
                                                  className='required form-label fs-7 mb-1'
                                                >
                                                  Description
                                                </label>
                                                <input
                                                  id='description'
                                                  name='description'
                                                  type='text'
                                                  className='form-control form-control-white form-select-sm custom-input-height'
                                                  placeholder='Description'
                                                  onBlur={handleBlur}
                                                  onChange={handleChange}
                                                  value={values.description}
                                                />
                                                {errors.description && touched?.description && (
                                                  <div className='fv-plugins-message-container'>
                                                    <div className='fv-help-block'>
                                                      <span role='alert'>{errors.description}</span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                            <div className='col-md-6'>
                                              <div className='mb-3'>
                                                <label
                                                  htmlFor='exampleFormControlInput1'
                                                  className='required form-label fs-7 mb-1'
                                                >
                                                  Amount
                                                </label>
                                                <input
                                                  id='amount'
                                                  name='amount'
                                                  type='number'
                                                  className='form-control form-control-white form-select-sm custom-input-height'
                                                  placeholder='Amount'
                                                  onBlur={handleBlur}
                                                  onChange={handleChange}
                                                  value={values.amount}
                                                />
                                                {errors.amount && touched?.amount && (
                                                  <div className='fv-plugins-message-container'>
                                                    <div className='fv-help-block'>
                                                      <span role='alert'>{errors.amount}</span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <div className='row d-flex'>
                                            <div className='col-md-12'>
                                              <div className='mb-3'>
                                                <label
                                                  htmlFor='exampleFormControlInput1'
                                                  className={`${currentUser?.result.userType !== 'SYSTEM' || !isOsiEdit ? 'required' : ''} form-label fs-7 mb-1`}
                                                >
                                                  File Upload
                                                </label>
                                                <input
                                                  type='file'
                                                  accept={SUPPORTED_UPLOAD_ACCEPT}
                                                  className='form-control form-control-white form-select-sm custom-input-height  '
                                                  placeholder='Select File'
                                                  onChange={(e) => onSelectExpenseFile(e, setFieldValue)}
                                                  name='file'
                                                  id='file'
                                                  onBlur={handleBlur}
                                                  ref={expenseFileInputRef}
                                                />
                                                {errors.file && (
                                                  <div className='fv-plugins-message-container'>
                                                    <div className='fv-help-block'>
                                                      <span role='alert'>{errors.file}</span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>

                                          {isOsiEdit ? <div className='row d-flex mt-3'>
                                            <div className='col-md-6'>
                                              <div className='form-check'>
                                                <input
                                                  className='form-check-input'
                                                  type='checkbox'
                                                  id='isExpensesPayableForInterpreter'
                                                  name='isExpensesPayableForInterpreter'
                                                  checked={values.isExpensesPayableForInterpreter}
                                                  onChange={handleChange}
                                                  disabled={readOnly || isInterpreterUser}
                                                />
                                                <label className='form-check-label' htmlFor='isExpensesPayableForInterpreter'>
                                                  Pay Interpreter
                                                </label>
                                              </div>
                                            </div>
                                            <div className='col-md-6'>
                                              <div className='form-check'>
                                                <input
                                                  className='form-check-input'
                                                  type='checkbox'
                                                  id='isExpensesChargeableForClient'
                                                  name='isExpensesChargeableForClient'
                                                  checked={values.isExpensesChargeableForClient}
                                                  onChange={handleChange}
                                                  disabled={readOnly || isInterpreterUser}
                                                />
                                                <label className='form-check-label' htmlFor='isExpensesChargeableForClient'>
                                                  Charge Client
                                                </label>
                                              </div>
                                            </div>
                                          </div> : null}
                                          <div className='row d-flex' style={{textAlign: 'end'}}>
                                            <div className='col-md-12'>
                                              <button
                                                onClick={() => handleSubmit()}
                                                className='btn btn-primary btn-sm'
                                                disabled={isSubmitting || !dirty || !isValid || hasInvalidExpenseFileUpload}
                                              >
                                                Add
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </Form>
                                      )
                                    }}
                                  </Formik>
                                )}
                                
                                <div className='row d-flex mt-3'>
                                  <div className='col-md-12 px-0'>
                                    <div className='accordion' id='kt_accordion_1'>
                                      {expenseList?.map((item: any, index: any) => (
                                        <div className='accordion-item'>
                                         
                                          <h2
                                            className='accordion-header'
                                            id={`kt_accordion_1_header_${index + 1}`}
                                          >
                                            <button
                                              className='accordion-button fs-6 fw-normal collapsed'
                                              type='button'
                                              data-bs-toggle='collapse'
                                              data-bs-target={`#kt_accordion_1_body_${index + 1}`}
                                              aria-expanded='false'
                                              aria-controls={`kt_accordion_1_body_${index + 1}`}
                                              style={{
                                                maxHeight: '50px',
                                                paddingTop: '10px',
                                                paddingBottom: '10px',
                                              }}
                                            >
                                               {item?.description}
                                            </button>
                                          </h2>
                                          <div
                                            id={`kt_accordion_1_body_${index + 1}`}
                                            className='accordion-collapse collapse'
                                            aria-labelledby={`kt_accordion_1_header_${index + 1}`}
                                            data-bs-parent='#kt_accordion_1'
                                          >
                                            <div className='accordion-body'>

                                              {readOnly || (isOsiEdit && !readOnly) ? (<div className='row d-flex mt-3'>
                                                <div className='col-md-6'>
                                                  <div className='form-check'>
                                                    <input
                                                      className='form-check-input'
                                                      type='checkbox'
                                                      id={`isExpensesPayableForInterpreter_${index}`}
                                                      name='isExpensesPayableForInterpreter'
                                                      checked={item.isExpensesPayableForInterpreter}
                                                      onChange={(e) => {
                                                        const newExpenseList = [...expenseList];
                                                        newExpenseList[index] = {
                                                          ...newExpenseList[index],
                                                          isExpensesPayableForInterpreter: e.target.checked
                                                        };
                                                        setExpenseList(newExpenseList);
                                                      }}
                                                      disabled={isInterpreterUser || readOnly}
                                                    />
                                                    <label className='form-check-label' htmlFor={`isExpensesPayableForInterpreter_${index}`}>
                                                      Pay Interpreter
                                                    </label>
                                                  </div>
                                                </div>
                                                <div className='col-md-6'>
                                                  <div className='form-check'>
                                                    <input
                                                      className='form-check-input'
                                                      type='checkbox'
                                                      id={`isExpensesChargeableForClient_${index}`}
                                                      name='isExpensesChargeableForClient'
                                                      checked={item.isExpensesChargeableForClient}
                                                      onChange={(e) => {
                                                        const newExpenseList = [...expenseList];
                                                        newExpenseList[index] = {
                                                          ...newExpenseList[index],
                                                          isExpensesChargeableForClient: e.target.checked
                                                        };
                                                        setExpenseList(newExpenseList);
                                                      }}
                                                      disabled={isInterpreterUser || readOnly}
                                                    />
                                                    <label className='form-check-label' htmlFor={`isExpensesChargeableForClient_${index}`}>
                                                      Charge Client
                                                    </label>
                                                  </div>
                                                </div>
                                              </div>
                                                ) : null}

                                              <div className='row d-flex align-items-center'>
                                                <div className='col-md-6'>
                                                  <span>
                                                    {' '}
                                                    <strong>Description -</strong>
                                                    {item?.description}{' '}
                                                  </span>
                                                </div>
                                                <div className='col-md-4'>
                                                  <strong>Amount -</strong>
                                                  <span>{item?.amount} </span>
                                                </div>
                                                <div
                                                  className='col-md-2'
                                                  style={{textAlign: 'end'}}
                                                >
                                                  {!readOnly && (
                                                    <a
                                                      onClick={() =>
                                                        setExpenseList((prev: any) =>
                                                          prev.filter(
                                                            (_: any, i: number) => i !== index
                                                          )
                                                        )
                                                      }
                                                      className='btn btn-icon btn-bg-light btn-color-danger btn-sm'
                                                    >
                                                      <KTSVG
                                                        path='/media/icons/duotune/general/gen027.svg'
                                                        className='svg-icon-3'
                                                      />
                                                    </a>
                                                  )}
                                                </div>
                                              </div>
                                              <div className='row d-flex mt-3'>
                                                {item?.code === '' ? (
                                                  <div className=' w-100 position-relative text-center '>
                                                    {item?.file?.name && getFileType(item?.file?.name) === 'pdf' ? (
                                                      <iframe
                                                        src={item?.dataUrl}
                                                        frameBorder='0'
                                                        width={'100%'}
                                                        height={'700px'}
                                                      />
                                                    ) : (
                                                      <img
                                                        src={item?.dataUrl}
                                                        alt='document'
                                                        className='w-100 h-auto'
                                                      />
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div className=' w-100 position-relative text-center '>
                                                    {item?.file && getFileType(item?.file) === 'pdf' ? (
                                                      <iframe
                                                        src={
                                                          process.env.REACT_APP_IMG_URL +
                                                          '/files/' +
                                                          initialValues.code +
                                                          '/' +
                                                          item?.file
                                                        }
                                                        frameBorder='0'
                                                        width={'100%'}
                                                        height={'700px'}
                                                      />
                                                    ) : (
                                                      <img
                                                        src={
                                                          process.env.REACT_APP_IMG_URL +
                                                          '/files/' +
                                                          initialValues.code +
                                                          '/' +
                                                          item?.file
                                                        }
                                                        alt='document'
                                                        className='w-100 h-auto'
                                                      />
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      
                                      </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> {/* Closes the inner div className='' */}
                  </div> {/* Closes the left column (col-lg-6) */}
                    {/* Submitted Details Panel - Now correctly placed as a sibling to the left column */}
                  {isDigitalVosEditLayout && meetingSummary && (
                    <div className='col-lg-6 col-12'>
                      <DigitalVosSubmittedDetailsPanel
                        meetingSummary={meetingSummary}
                        selectedSource={selectedVosSource}
                        onSelectClient={applyClientVosToForm}
                        onSelectInterpreter={applyInterpreterVosToForm}
                      />
                    </div>
                  )}

                  </div> {/* Closes the main row wrapper */}
                  
                  {isDigitalVosEditLayout && (
                    <p className='text-muted small mt-3 mb-0'>
                      <span className='fw-bold'>TIP:</span> Please select the correct form from right
                      panel to copy the details. You can edit afterwards.
                    </p>
                  )}
                </div>
                <div className='modal-footer py-3 d-flex justify-content-between align-items-center w-100'>
                  {readOnly && isApprovedStatus && isSystemUser && (
                    <>
                      {!showRevertConfirm ? (
                        <>
                          <button
                            type='button'
                            className='btn btn-link btn-color-danger btn-active-color-danger p-0 fw-bold fs-7'
                            style={{textDecoration: 'underline', color: '#dc3545', background: 'none', border: 'none'}}
                            onClick={onEditWithoutUnlinking}
                          >
                            Edit Without Unlinking
                          </button>
                          <div className='ms-auto'>
                            <button
                              type='button'
                              className='btn btn-danger btn-sm'
                              onClick={() => setShowRevertConfirm(true)}
                            >
                              Revert Approval
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className='d-flex flex-column align-items-center w-100'>
                          <p className='text-center mb-3'>
                            This will move the status to submitted and unlink the appointment from invoice.
                          </p>
                          <div className='d-flex gap-2'>
                            <button
                              type='button'
                              className='btn btn-light btn-sm'
                              onClick={() => setShowRevertConfirm(false)}
                            >
                              Cancel
                            </button>
                            <button
                              type='button'
                              className='btn btn-danger btn-sm'
                              data-bs-dismiss='modal'
                              onClick={() => {
                                setShowRevertConfirm(false)
                                onRevertApproval?.()
                              }}
                            >
                              Proceed
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {!readOnly && (
                    <>
                      <button
                        type='reset'
                        className='btn btn-light btn-sm'
                        data-bs-dismiss='modal'
                        onClick={() => {
                          setIsEditWithoutUnlinking?.(false)
                          setShowOsiReadOnly?.(true)
                          verifiedSubmitRef.current = false
                          setShowTimeVerification(false)
                          resetVosFileUploadState()
                          resetExpenseFileUploadState()
                          resetForm()
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type='button'
                        className='btn btn-primary btn-sm'
                        disabled={
                          vosExtracting ||
                          isSubmitting ||
                          hasInvalidVosFileUpload ||
                          !isValid ||
                          (!showVosUploadInLayout &&
                            values.submissionTab === 'manual' &&
                            !initialValues?.code &&
                            !values.vosForm)
                        }
                        onClick={() => handleSubmit()}
                      >
                        <span className='indicator-label'>
                          {useUpdateAndApprove
                            ? 'Update & Approve'
                            : isEditWithoutUnlinking
                              ? 'Update & Submit'
                              : `${!initialValues?.code ? 'Save' : 'Update'} & Submit`}
                        </span>
                        {(isSubmitting || vosExtracting) && (
                          <span className='indicator-progress'>
                            Please wait...
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </>
                  )}
                </div>
                {showTimeVerification && (
                  <div
                    className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
                    style={{zIndex: 1065, backgroundColor: 'rgba(0,0,0,0.35)'}}
                  >
                    <div
                      className='bg-white rounded shadow border border-danger'
                      style={{maxWidth: '560px', width: 'calc(100% - 32px)', overflow: 'hidden'}}
                    >
                      <div className='d-flex gap-4 p-8 pb-6'>
                        <div
                          className='rounded-circle d-flex align-items-center justify-content-center bg-light-danger flex-shrink-0'
                          style={{width: '64px', height: '64px'}}
                        >
                          <i className='bi bi-exclamation-circle text-danger fs-1'></i>
                        </div>
                        <div>
                          <h3 className='fw-bold mb-4'>Mandatory Verification Required</h3>
                          <p className='fs-5 text-gray-700 mb-0'>
                            Based on the Start and End Time, Your duration is{' '}
                            {timeVerificationDuration}. Please verify the time to avoid any
                            mismatch of duration.
                          </p>
                        </div>
                      </div>
                      <div className='d-flex justify-content-end gap-3 p-5 border-top'>
                        <button
                          type='button'
                          className='btn btn-light btn-sm px-7'
                          onClick={() => {
                            verifiedSubmitRef.current = false
                            setShowTimeVerification(false)
                          }}
                        >
                          Review
                        </button>
                        <button
                          type='button'
                          className='btn btn-primary btn-sm px-7'
                          onClick={() => {
                            verifiedSubmitRef.current = true
                            setShowTimeVerification(false)
                            handleSubmit()
                          }}
                        >
                          Verified
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )
          }}
        </Formik>
      </div>
    </div>

  </>
  )
}

export default AddOSIDetails